"use client";

import React from "react";
import { Landmark, Flag, FileText, House, MapPin, Award, Building2, ExternalLink } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const resources = [{
  title: "HUD.gov",
  description: "The official resource hub for the U.S. Department of Housing and Urban Development. Access comprehensive guides on home rights and housing assistance.",
  icon: Building2,
  url: "https://www.hud.gov"
}, {
  title: "USA.gov",
  description: "Your online guide to government information and services. Find housing help, government agencies, and contact elected officials.",
  icon: Flag,
  url: "https://www.usa.gov"
}, {
  title: "AnnualCreditReport.com",
  description: "The only source for your free credit reports. Authorized by Federal law, this tool helps you monitor your financial health before buying.",
  icon: FileText,
  url: "https://www.annualcreditreport.com"
}, {
  title: "FHA Loans",
  description: "Learn about Federal Housing Administration loans which can help first-time buyers with lower down payments and credit score requirements.",
  icon: House,
  url: "https://www.hud.gov/buying/loans"
}, {
  title: "SONYMA",
  description: "State of New York Mortgage Agency offers competitive fixed-rate mortgages and down payment assistance for first-time homebuyers in NY.",
  icon: MapPin,
  url: "https://hcr.ny.gov/sonyma"
}, {
  title: "VA Loans",
  description: "Exclusive housing benefits for Veterans and Service members. Discover loans with favorable terms provided by the Department of Veterans Affairs.",
  icon: Award,
  url: "https://www.va.gov/housing-assistance/home-loans/"
}, {
  title: "FHLB New York",
  description: "The Federal House Loan Bank of New York supports community lending and provides grant programs for first-time homebuyer assistance.",
  icon: Landmark,
  url: "https://www.fhlbny.com"
}];
export default function EssentialResources() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="essentialresources" ref={ref} className="bg-black py-20 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-black via-[#FFD700] to-black opacity-50" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-serif mb-4 tracking-tight">
            Essential Resources for First-Time House Buyers
          </h2>
          <div className="w-24 h-1 bg-[#FFD700] mx-auto mb-6" />
          <p className="text-[#FFD700] text-lg md:text-xl font-medium max-w-2xl mx-auto">
            Navigate your journey to homeownership with these trusted government and financial tools.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {resources.map((resource, i) => <div key={resource.title} data-index={i} className={`transition-all duration-700 delay-[${i * 100}ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Card className="h-full bg-black border border-[#333333] hover:border-[#DC143C] transition-colors duration-300 group flex flex-col">
                <CardHeader>
                  <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/20 group-hover:bg-[#FFD700] group-hover:text-black text-[#FFD700] transition-colors duration-300">
                    <resource.icon className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-xl font-bold text-white font-serif group-hover:text-[#FFD700] transition-colors">
                    {resource.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardDescription className="text-gray-300 text-sm leading-relaxed">
                    {resource.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pt-4">
                  <Link to={resource.url} newTab className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-9 px-4 py-2 has-[>svg]:px-3 w-full bg-primary border border-transparent transition-all duration-300 font-medium !text-[hsl(0,0%,0%)] !opacity-[1] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-[#FFD700] hover:text-black">
                    <span>Access Resource</span>
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Link>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}